import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, Filter, ChevronDown, MoreVertical, Shield,
  MapPin, Globe, Building2, Settings, Check,
  Plus, X, Edit, Trash2, Eye, User, FileText,
  Users, ArrowUpDown, UserCheck, Home, Info,
  Map, PinIcon, Navigation, ArrowUpRight, BadgeCheck,
  GridIcon, Layers, GlobeIcon, Clock, CheckCircle
} from "lucide-react";

// Google Maps types
declare global {
  interface Window {
    google: any;
  }
}
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import AdminHeader from "../../Headers/AdminHeader";
import SharedFooter3 from "../../Footer/SharedFooter3";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { getAllServiceAreas, deleteServiceArea } from "../../redux/Actions/ServiceAreaAction";
import { getAllCountries } from "../../redux/Actions/AdminAction";
import { selectLoaderValue, showLoader, hideLoader } from "../../redux/Slices/loadingSlice";
import ServiceAreaModal from "./ServiceAreaModal";

// Service Area Type Enum
const ServiceAreaType = {
  METROPOLITAN: 1,
  CITY: 2,
  REGION: 3,
  PROVINCE: 4
};

const ServiceAreas = () => {
  // Redux hooks
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector(selectLoaderValue);
  const { serviceAreas } = useAppSelector((state: any) => state.serviceArea);
  const { countries: reduxCountries } = useAppSelector((state: any) => state.admin);

  // State management
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState("all");
  const [selectedType, setSelectedType] = useState("all");
  const [showAreaDetails, setShowAreaDetails] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [sortBy, setSortBy] = useState("createdAt");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [shouldFetchData, setShouldFetchData] = useState(false);
  const isInitializedRef = useRef(false);

  // Countries state management
  const [allCountries, setAllCountries] = useState([]);
  const [countriesLoading, setCountriesLoading] = useState(false);
  const [countriesTotalCount, setCountriesTotalCount] = useState(0);
  const [countrySearchQuery, setCountrySearchQuery] = useState("");

  // Initialize state from localStorage on component mount
  useEffect(() => {
    const savedPage = localStorage.getItem("serviceAreas_currentPage");
    const savedLimit = localStorage.getItem("serviceAreas_limit");
    
    const pageToUse = savedPage ? parseInt(savedPage) : 1;
    const limitToUse = savedLimit ? parseInt(savedLimit) : 10;
    
    // Set state with localStorage values
    setCurrentPage(pageToUse);
    setLimit(limitToUse);
    
    // Mark as initialized and trigger data fetch
    isInitializedRef.current = true;
    setShouldFetchData(true);
    
    // Fetch all countries
    fetchAllCountries();
  }, []);

  // Function to fetch all countries in chunks of 50
  const fetchAllCountries = async (forceRefresh = false) => {
    if (allCountries.length > 0 && !forceRefresh) {
      return;
    }

    setCountriesLoading(true);
    try {
      let allCountriesData = [];
      let currentPage = 1;
      let hasNextPage = true;
      const chunkSize = 50;

      while (hasNextPage) {
        const result = await dispatch(getAllCountries({ 
          page: currentPage, 
          limit: chunkSize, 
          isShowLoader: false 
        }));

        if (result.meta.requestStatus === "fulfilled" && result.payload) {
          const response = result.payload;
          const countriesData = response.data || [];
          
          // Add countries from this chunk
          allCountriesData = [...allCountriesData, ...countriesData];
          
          // Update total count on first page
          if (currentPage === 1) {
            setCountriesTotalCount(response.totalCount || 0);
          }
          
          // Check if there are more pages
          hasNextPage = response.hasNextPage || false;
          currentPage++;
        } else {
          break;
        }
      }

      // Sort countries alphabetically by name
      allCountriesData.sort((a, b) => a.name.localeCompare(b.name));
      
      setAllCountries(allCountriesData);
      
    } catch (error) {
      // Show error toast or handle error appropriately
    } finally {
      setCountriesLoading(false);
    }
  };

  // Function to refresh countries (useful for adding new countries)
  const refreshCountries = () => {
    setAllCountries([]);
    fetchAllCountries(true);
  };

  // Single useEffect to handle all API calls
  useEffect(() => {
    if (shouldFetchData && isInitializedRef.current) {
      getAllCurrentServiceAreas(currentPage, limit);
    }
  }, [shouldFetchData, currentPage, limit, debouncedSearchQuery, selectedType, selectedCountry, sortBy]);

  // Update pagination when service areas data changes
  useEffect(() => {
    if (serviceAreas && serviceAreas.totalCount) {
      const calculatedTotalPages = Math.ceil(serviceAreas.totalCount / limit);
      setTotalPages(calculatedTotalPages);
      
      // Only adjust current page if we've initialized and the page is invalid
      if (isInitializedRef.current && currentPage > calculatedTotalPages && calculatedTotalPages > 0) {
        setCurrentPage(calculatedTotalPages);
        localStorage.setItem("serviceAreas_currentPage", calculatedTotalPages.toString());
      }
    }
  }, [serviceAreas, limit, currentPage]);

  // Handle limit changes to recalculate current page if needed
  useEffect(() => {
    if (isInitializedRef.current && shouldFetchData) {
      // If changing limit, we need to recalculate if current page is still valid
      if (serviceAreas && serviceAreas.totalCount) {
        const calculatedTotalPages = Math.ceil(serviceAreas.totalCount / limit);
              if (currentPage > calculatedTotalPages && calculatedTotalPages > 0) {
        setCurrentPage(calculatedTotalPages);
        localStorage.setItem("serviceAreas_currentPage", calculatedTotalPages.toString());
      }
      }
    }
  }, [limit, serviceAreas, currentPage, shouldFetchData]);



  const getAllCurrentServiceAreas = async (page = 1, limit = 10) => {
    dispatch(showLoader());
    await dispatch(getAllServiceAreas({ 
      page, 
      limit, 
      isShowLoader: false,
      search: debouncedSearchQuery,
      sortBy: sortBy,
      sortOrder: "desc",
      type: selectedType,
      country: selectedCountry
    }));
    dispatch(hideLoader());
  };

  // Pagination handlers
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      localStorage.setItem("serviceAreas_currentPage", newPage.toString());
    }
  };

  const handleFirstPage = () => handlePageChange(1);
  const handleLastPage = () => handlePageChange(totalPages);
  const handleNextPage = () => handlePageChange(currentPage + 1);
  const handlePrevPage = () => handlePageChange(currentPage - 1);

  const handleGoToPage = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const pageInput = formData.get("page") as string;
    const pageNumber = parseInt(pageInput);
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      handlePageChange(pageNumber);
    }
  };


  // Pagination Component
  const Pagination = () => {
    // Show pagination if there are results, even if only one page
    if (!serviceAreas || !serviceAreas.data || serviceAreas.data.length === 0) return null;



    const getPageNumbers = () => {
      const pages = [];
      const maxVisiblePages = 5;
      let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
      const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

      if (endPage - startPage + 1 < maxVisiblePages) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      return pages;
    };

    const pageNumbers = getPageNumbers();

    return (
      <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
        <div className="flex items-center justify-between w-full">
          {/* Mobile view */}
          <div className="flex items-center justify-between w-full sm:hidden">
            {totalPages > 1 ? (
              <>
                <button
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <div className="flex flex-col items-center">
                  <span className="text-sm text-gray-700">
                    Page {currentPage} of {totalPages}
                  </span>
                  <div className="flex items-center gap-1 mt-1">
                    <span className="text-xs text-gray-500">Show:</span>
                    <select
                      value={limit}
                      onChange={(e) => {
                        const newLimit = parseInt(e.target.value);
                        setLimit(newLimit);
                        // Don't reset page when changing limit - keep current page
                      }}
                      className="text-xs border border-gray-300 rounded px-1 py-0.5"
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                    </select>
                  </div>
                </div>
                <button
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </>
            ) : (
              <div className="flex flex-col items-center w-full">
                <span className="text-sm text-gray-700">
                  Showing {serviceAreas.data.length} of {serviceAreas.totalCount} results
                </span>
                <div className="flex items-center gap-1 mt-1">
                  <span className="text-xs text-gray-500">Show:</span>
                  <select
                    value={limit}
                                      onChange={(e) => {
                    const newLimit = parseInt(e.target.value);
                    setLimit(newLimit);
                    // Don't reset page when changing limit - keep current page
                  }}
                    className="text-xs border border-gray-300 rounded px-1 py-0.5"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* Desktop view */}
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{((currentPage - 1) * limit) + 1}</span> to{" "}
                <span className="font-medium">
                  {Math.min(currentPage * limit, serviceAreas.totalCount || 0)}
                </span>{" "}
                of <span className="font-medium">{serviceAreas.totalCount || 0}</span> results
              </p>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700">Show:</span>
                <select
                  value={limit}
                                        onChange={(e) => {
                        const newLimit = parseInt(e.target.value);
                        setLimit(newLimit);
                        // Don't reset page when changing limit - keep current page
                      }}
                  className="text-sm border border-gray-300 rounded-md px-2 py-1"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
                <span className="text-sm text-gray-700">per page</span>
              </div>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-700">Go to page:</span>
                  <form onSubmit={handleGoToPage} className="flex items-center gap-1">
                    <input
                      type="number"
                      name="page"
                      min="1"
                      max={totalPages}
                      defaultValue={currentPage}
                      className="w-16 text-sm border border-gray-300 rounded-md px-2 py-1 text-center"
                    />
                    <button
                      type="submit"
                      className="text-sm bg-blue-600 text-white px-2 py-1 rounded-md hover:bg-blue-700"
                    >
                      Go
                    </button>
                  </form>
                </div>
              )}
            </div>
            <div>
              {totalPages > 1 && (
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  {/* First Page */}
                  <button
                    onClick={handleFirstPage}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">First</span>
                    <ChevronDown className="h-5 w-5 rotate-90" />
                  </button>

                  {/* Previous Page */}
                  <button
                    onClick={handlePrevPage}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Previous</span>
                    <ChevronDown className="h-5 w-5 rotate-90" />
                  </button>

                  {/* Page Numbers */}
                  {pageNumbers.map((pageNumber) => (
                    <button
                      key={pageNumber}
                      onClick={() => handlePageChange(pageNumber)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        pageNumber === currentPage
                          ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                          : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                      }`}
                    >
                      {pageNumber}
                    </button>
                  ))}

                  {/* Next Page */}
                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Next</span>
                    <ChevronDown className="h-5 w-5 -rotate-90" />
                  </button>

                  {/* Last Page */}
                  <button
                    onClick={handleLastPage}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="sr-only">Last</span>
                    <ChevronDown className="h-5 w-5 -rotate-90" />
                  </button>
                </nav>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

    // Use service areas data directly since filtering is now handled by the backend
  const filteredServiceAreas = serviceAreas.data || [];

  // Debounce search query to prevent too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Save limit to localStorage when it changes
  useEffect(() => {
    localStorage.setItem("serviceAreas_limit", limit.toString());
  }, [limit]);

  // This useEffect is no longer needed as we handle initialization in the first useEffect

  // Cleanup localStorage on component unmount (optional)
  useEffect(() => {
    return () => {
      // Optionally clear localStorage when component unmounts
      // Uncomment the line below if you want to clear on unmount
      // localStorage.removeItem('serviceAreas_currentPage');
      // localStorage.removeItem('serviceAreas_limit');
    };
  }, []);

  // Reset to page 1 when filters change
  useEffect(() => {
    if (shouldFetchData) {
      setCurrentPage(1);
      localStorage.setItem("serviceAreas_currentPage", "1");
    }
  }, [debouncedSearchQuery, selectedType, selectedCountry, sortBy]);

  // Listen for new countries being added to Redux state and refresh local state
  useEffect(() => {
    if (reduxCountries && reduxCountries.length > allCountries.length && allCountries.length > 0) {
      refreshCountries();
    }
  }, [reduxCountries]);

  // Service Area Card Component
  const ServiceAreaCard = ({ area }) => {
    // Add null checks and fallbacks
    if (!area) return null;

    const getTypeName = (typeId) => {
      switch (parseInt(typeId)) {
        case ServiceAreaType.METROPOLITAN:
          return "Metropolitan";
        case ServiceAreaType.CITY:
          return "City";
        case ServiceAreaType.REGION:
          return "Region";
        case ServiceAreaType.PROVINCE:
          return "Province";
        default:
          return "Unknown";
      }
    };

    const typeName = area.type?.id || area.type || ServiceAreaType.METROPOLITAN;
    const displayType = getTypeName(typeName);
    const cities = Array.isArray(area.cities) ? area.cities : [];
    const serviceTypes = Array.isArray(area.serviceTypes) ? area.serviceTypes : [];
    const radius = typeof area.radius === "number" ? area.radius.toString() : (area.radius || "0");
    const province = area.province || "Unknown";
    const name = area.name || "Unnamed Area";

    return (
      <Card className="hover:shadow-md transition-shadow">
        <div className="p-6 space-y-4" style={{ textAlign: "left" }}>
          {/* Header */}
          <div className="flex justify-between items-start">
            <div>
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                  {displayType}
                </Badge>
                <Badge variant={undefined} className="bg-green-100 text-green-700">
                  ACTIVE
                </Badge>
              </div>
              <h3 className="text-lg font-semibold mt-2">{name}</h3>
              <p className="text-sm text-gray-500">{province}, {area.country?.name || "Unknown Country"}</p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAreaDetails(area)}
                className="text-gray-600 hover:text-gray-900"
              >
                <Edit className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-red-600 hover:text-red-900"
                onClick={async () => {
                  if (window.confirm("Are you sure you want to delete this service area?")) {
                    try {
                      const deleteResult = await dispatch(deleteServiceArea({ id: area.id }));
                      // The Redux state will be updated automatically by the reducer on success
                      // If there's an error, it will be handled by the rejectWithValue in the action
                    } catch (error) {
                    }
                  }
                }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Coverage Info */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Coverage Area</h4>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <MapPin className="h-4 w-4 text-gray-400" />
              <span>Radius: {radius}km</span>
            </div>
            {(area.latitude || area.longitude) && (
              <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                <Globe className="h-4 w-4 text-gray-400" />
                <span>
                  {area.latitude?.toFixed(6)}, {area.longitude?.toFixed(6)}
                </span>
              </div>
            )}
            <div className="mt-2 flex flex-wrap gap-2">
              {cities.slice(0, 3).map((city, index) => (
                <Badge key={index} variant="outline">
                  {city}
                </Badge>
              ))}
              {cities.length > 3 && (
                <Badge variant="outline">
                  +{cities.length - 3} more
                </Badge>
              )}
            </div>
          </div>

          {/* Service Types */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Available Services</h4>
            <div className="flex flex-wrap gap-2">
              {serviceTypes.map((service, index) => (
                <Badge variant={undefined} key={index} className="bg-green-100 text-green-700">
                  {service}
                </Badge>
              ))}
            </div>
          </div>

          {/* Emergency Services */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Emergency Services</h4>
            <div className="flex items-center gap-2">
              {area.emergencyServices ? (
                <Badge variant="outline" className="bg-green-100 text-green-700">
                  <Check className="h-3 w-3 mr-1" />
                  Enabled
                </Badge>
              ) : (
                <Badge variant="outline" className="bg-gray-100 text-gray-700">
                  Disabled
                </Badge>
              )}
            </div>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" style={{ textAlign: "left" }}>
      <AdminHeader />

      <main className="flex-1 lg:pl-64 pt-16" style={{ textAlign: "left" }}>
        <div className="p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Service Areas</h1>
                <p className="text-gray-600 mt-1">
                  Manage and monitor service coverage areas
                  {countriesLoading && (
                    <span className="ml-2 inline-flex items-center gap-1 text-sm text-blue-600">
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
                      Loading countries...
                    </span>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => setShowAddModal(true)}
                  className="flex items-center gap-2"
                  disabled={countriesLoading}
                >
                  <Plus className="h-4 w-4" />
                  Add Area
                </Button>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Total Areas</p>
                    <p className="text-2xl font-semibold">{serviceAreas.totalCount}</p>
                  </div>
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Map className="h-5 w-5 text-blue-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Active Areas</p>
                    <p className="text-2xl font-semibold">{serviceAreas.data.length}</p>
                  </div>
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Total Coverage</p>
                    <p className="text-2xl font-semibold">85%</p>
                  </div>
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Globe className="h-5 w-5 text-purple-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Active Providers</p>
                    <p className="text-2xl font-semibold">450</p>
                  </div>
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <Users className="h-5 w-5 text-yellow-600" />
                  </div>
                </div>
              </Card>
            </div>

            {/* Search and Filters */}
            <Card className="p-4">
              <div className="flex flex-col sm:flex-row justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="relative flex-1 sm:min-w-[300px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search areas..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border rounded-lg"
                    />
                    {searchQuery !== debouncedSearchQuery && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                      </div>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setShowFilters(!showFilters)}
                    className="flex items-center gap-2"
                  >
                    <Filter className="h-5 w-5" />
                    Filters
                  </Button>
                </div>

                <div className="flex items-center gap-4">
                  <select
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value)}
                    className="px-4 py-2 border rounded-lg"
                  >
                    <option value="all">All Types</option>
                    <option value={ServiceAreaType.METROPOLITAN}>Metropolitan</option>
                    <option value={ServiceAreaType.CITY}>City</option>
                    <option value={ServiceAreaType.REGION}>Region</option>
                    <option value={ServiceAreaType.PROVINCE}>Province</option>
                  </select>

                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="px-4 py-2 border rounded-lg"
                  >
                    <option value="createdAt">Sort by Date Created</option>
                    <option value="name">Sort by Name</option>
                    <option value="province">Sort by Province</option>
                    <option value="radius">Sort by Coverage Radius</option>
                  </select>
                </div>
              </div>
            </Card>

            {/* Service Areas Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {filteredServiceAreas.map(area => (
                <ServiceAreaCard key={area.id} area={area} />
              ))}
            </div>

            {/* Pagination */}
            {!isLoading && serviceAreas && serviceAreas.data && serviceAreas.data.length > 0 && <Pagination />}

            {/* Empty State */}
            {filteredServiceAreas.length === 0 && !isLoading && (
              <div className="text-center py-12">
                <Map className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {debouncedSearchQuery ? "No service areas found" : "No service areas found"}
                </h3>
                <p className="text-gray-500 mb-4">
                  {debouncedSearchQuery 
                    ? `No service areas match your search for "${debouncedSearchQuery}". Try adjusting your search terms.`
                    : "Get started by creating your first service area."
                  }
                </p>
                {!debouncedSearchQuery && (
                  <Button onClick={() => setShowAddModal(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Service Area
                  </Button>
                )}
              </div>
            )}

            {/* Loading State */}
            {isLoading && (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-500 mt-2">Loading service areas...</p>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Modal */}
      <ServiceAreaModal
        area={showAreaDetails}
        isOpen={!!showAreaDetails || showAddModal}
        onClose={() => {
          setShowAreaDetails(null);
          setShowAddModal(false);
        }}
      />

      {/* <SharedFooter3 /> */}
    </div>
  );
};

export default ServiceAreas;